import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import cn from 'classnames';
import { Range, getTrackBackground } from './Range';
import useDeviceResize from '../../../hooks/useDeviceResize';
import { efesOrange, rangeGray } from '../../../theme';

import useStyles from './makeStyles';

const getClosestMark = (value, marks) => marks.reduce((prev, curr) => (
  Math.abs(curr.value - value) < Math.abs(prev.value - value) ? curr : prev
));

const MainRange = ({ marks, step, min, max, values, setValues }) => {
  const classes = useStyles();
  const isDeviceMobileSize = useDeviceResize('sm');
  const [rangeValue, setRangeValue] = useState(values);

  useEffect(() => {
    setRangeValue(values);
  }, [values]);

  const debouncedSetValues = React.useMemo(
    () => debounce((newValues) => setValues(newValues), 200),
    [setValues],
  );

  const handleChange = (newValues) => {
    const closestMark = getClosestMark(newValues[0], marks);
    debouncedSetValues([closestMark.value]);
    setRangeValue([closestMark.value]);
  };

  return (
    <div className={classes.root}>
      <Range
        values={rangeValue}
        step={step}
        min={min}
        max={max}
        onChange={handleChange}
        renderMark={({ props, index }) => {
          const mark = marks[index];
          if (!mark) return null;

          const markPosition = ((mark.value - min) / (max - min)) * 100;
          const isSelected = mark.value === rangeValue[0];
          const thumbPosition = ((rangeValue[0] - min) / (max - min)) * 100;
          const isOppositeSide = markPosition < thumbPosition;

          return (
            <div
              {...props}
              className={classes.mark}
              style={{
                ...props.style,
                left: `${markPosition}%`,
                transform: 'translateX(-50%)',
                backgroundColor: mark.value <= rangeValue[0] ? efesOrange : rangeGray,
                border: isOppositeSide && !isSelected ? `1px solid ${efesOrange}` : '1px solid #fff',
              }}
            >
              <div
                style={{
                  transform: index === marks.length - 1 ? 'translateX(-80%)' : '',
                }}
                className={cn(classes.label, {
                  [classes.selectedLabel]: isSelected,
                })}
              >
                {isDeviceMobileSize ? mark.mobileLabel : mark.label}
              </div>
            </div>
          );
        }}
        renderTrack={({ props, children }) => (
          <div
            onMouseDown={props.onMouseDown}
            className={classes.trackContainer}
            style={props.style}
            role="presentation"
          >
            <div
              ref={props.ref}
              className={classes.track}
              style={{
                background: getTrackBackground({
                  values: rangeValue,
                  colors: [efesOrange, rangeGray],
                  min,
                  max,
                }),
              }}
            >
              {children}
            </div>
          </div>
        )}
        renderThumb={({ props }) => (
          <div {...props} className={classes.thumb}>
            <div
              className={classes.thumbInner}
              style={{
                backgroundColor: efesOrange,
              }}
            />
          </div>
        )}
        marks={marks.map((mark) => mark.value)}
      />
    </div>
  );
};

MainRange.propTypes = {
  marks: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
    mobileLabel: PropTypes.string,
  })).isRequired,
  step: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  values: PropTypes.arrayOf(PropTypes.number).isRequired,
  setValues: PropTypes.func.isRequired,
  style: PropTypes.shape({}),
  onMouseDown: PropTypes.func,
  ref: PropTypes.shape({}),
};

MainRange.defaultProps = {
  style: {},
  onMouseDown: () => {},
  ref: {},
};

export default MainRange;
